import { useEffect, useRef } from 'react'
import type { Product } from '@/types'
import type { LineSelection, ModifierOptionPrice } from '@/lib/lineModifiers'
import { resolveLineUnitPrice } from '@/lib/lineModifiers'
import { selectionSignature } from './kotLineOptions'
import type { KOTItem } from './KOTServerInterface'

/**
 * Local persistence for the floor ticket a server is building (F-FE-N109).
 *
 * The counter has had this since F-FE-N31 (`useCounterDraft`); the floor never
 * did. On a desktop terminal that gap was invisible — a cashier lives on one
 * screen. On the Bhookly Server tablet a server moves between the floor and the
 * counter constantly, and every move unmounts the screen they left. A ticket
 * with eight unsent lines on it simply evaporated.
 *
 * Same discipline as the counter's draft, for the same reasons:
 *
 *  - product id + quantity + options only. NEVER the Product (it carries a
 *    multi-MB base64 image) and NEVER a price. The line is re-priced from the
 *    live menu on restore, so a price change between staging and restoring is
 *    picked up rather than a stale figure resurrected.
 *  - namespaced by user, so two servers sharing a tablet cannot inherit each
 *    other's table.
 *  - aged out after one shift; a ticket older than that is noise.
 *
 * Only UNSENT lines are persisted. Anything fired already lives on the server
 * and comes back from `getOrder` — persisting it too would ring the same round
 * twice, which is the one mistake this feature must not introduce.
 */

const TICKET_KEY_PREFIX = 'pos-server-ticket-v1'
/** A ticket older than one shift is almost always noise (and stale prices). */
export const TICKET_MAX_AGE_MS = 12 * 60 * 60 * 1000

export interface PersistedTicketLine {
  productId: string
  quantity: number
  special_instructions?: string
  course_number?: number
  selections?: LineSelection[]
}

export interface PersistedTicket {
  version: 1
  userId: string
  savedAt: number
  /** Set once the ticket has been fired at least once; null for a fresh table. */
  orderId: string | null
  tableId: string | null
  tableName: string | null
  guestCount: number
  orderNotes: string
  /** Unsent lines only. */
  lines: PersistedTicketLine[]
}

export type TicketFields = Omit<PersistedTicket, 'version' | 'userId' | 'savedAt'>

function ticketKey(userId: string): string {
  return `${TICKET_KEY_PREFIX}:${userId}`
}

/** A ticket with no unsent lines has nothing to lose. */
function isEmptyTicket(fields: TicketFields): boolean {
  return fields.lines.length === 0
}

export function readTicket(userId: string): PersistedTicket | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(ticketKey(userId))
    if (!raw) return null
    const t = JSON.parse(raw) as PersistedTicket
    if (t.version !== 1 || t.userId !== userId || !Array.isArray(t.lines)) return null
    if (typeof t.savedAt !== 'number' || Date.now() - t.savedAt > TICKET_MAX_AGE_MS) return null
    return t
  } catch {
    return null
  }
}

export function clearTicket(userId: string | null): void {
  if (typeof window === 'undefined' || !userId) return
  try {
    localStorage.removeItem(ticketKey(userId))
  } catch {
    /* ignore */
  }
}

/** Exported for the autosave hook and its tests; app code uses the hook. */
export function writeTicket(userId: string, fields: TicketFields): void {
  if (typeof window === 'undefined') return
  try {
    if (isEmptyTicket(fields)) {
      localStorage.removeItem(ticketKey(userId))
      return
    }
    const payload: PersistedTicket = {
      version: 1,
      userId,
      savedAt: Date.now(),
      ...fields,
    }
    localStorage.setItem(ticketKey(userId), JSON.stringify(payload))
  } catch {
    /* localStorage full/blocked — non-fatal; the ticket still lives in state */
  }
}

/**
 * Debounced autosave of the floor ticket, flushed on tab-hide, on unload, and
 * — the case this exists for — on UNMOUNT.
 *
 * The unmount flush is not belt-and-braces, it is the main event. Leaving this
 * screen for the counter is a client-side route change: React unmounts the
 * component and neither `beforeunload` nor `visibilitychange` ever fires. Worse,
 * the debounce timer's own cleanup CANCELS the pending write. Without a flush
 * here, everything a server did in the last half-second before switching screens
 * would be dropped by the very mechanism meant to save it.
 */
export function useTicketAutosave(
  userId: string | null,
  fields: TicketFields,
  enabled: boolean,
): void {
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields
  // Read at flush time, not captured: `enabled` goes false the moment the
  // ticket stops being ours to save, and the unmount flush must respect that.
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  useEffect(() => {
    if (!enabled || !userId) return
    const t = window.setTimeout(() => writeTicket(userId, fieldsRef.current), 500)
    return () => window.clearTimeout(t)
  }, [enabled, userId, fields])

  useEffect(() => {
    if (!userId) return
    const flush = () => {
      if (enabledRef.current) writeTicket(userId, fieldsRef.current)
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('beforeunload', flush)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('beforeunload', flush)
      document.removeEventListener('visibilitychange', onVisibility)
      flush()
    }
  }, [userId])
}

/** The unsent lines of a ticket, in the shape that gets persisted. */
export function toPersistedLines(
  items: ReadonlyArray<KOTItem>,
  isUnsent: (status: string) => boolean,
): PersistedTicketLine[] {
  const out: PersistedTicketLine[] = []
  for (const i of items) {
    if (!isUnsent(i.status)) continue
    const line: PersistedTicketLine = { productId: i.product_id, quantity: i.quantity }
    if (i.special_instructions) line.special_instructions = i.special_instructions
    if (i.course_number != null && i.course_number !== 1) line.course_number = i.course_number
    if (i.selections.length > 0) line.selections = i.selections
    out.push(line)
  }
  return out
}

/**
 * Rebuild persisted lines against the live menu.
 *
 * Products that left the menu (or went unavailable) are dropped and counted, as
 * are options the menu no longer offers — the server is told rather than the
 * guest being quietly charged the base price. Every surviving line is RE-PRICED
 * through `resolveLineUnitPrice`, the same function the counter and the backend
 * agree on, so nothing here can resurrect yesterday's price.
 *
 * Ids are minted fresh: a `draft-` id is render identity, not something worth
 * persisting, and `refreshOrder` keys on that prefix to tell a local line from
 * a server one.
 */
export function restoreTicketLines(
  lines: PersistedTicketLine[],
  liveProducts: Product[],
  /** F-MOD-N13: per product — a shared option can be priced per item. */
  priceIndexFor: (productId: string) => ReadonlyMap<string, ModifierOptionPrice>,
  seq: () => string,
): { items: KOTItem[]; dropped: number } {
  const byId = new Map(liveProducts.map((p) => [p.id, p]))
  const items: KOTItem[] = []
  let dropped = 0

  for (const line of lines) {
    const product = byId.get(line.productId)
    if (!product || product.is_available === false) {
      dropped++
      continue
    }
    const persisted = line.selections ?? []
    const priceIndex = priceIndexFor(product.id)
    const selections = persisted.filter((s) => priceIndex.has(s.option_id))
    if (selections.length !== persisted.length) dropped++

    const unitPrice = resolveLineUnitPrice(product.price, selections, priceIndex)
    const sig = selectionSignature(selections)
    // Fold on restore for the same reason the reducer folds on add: dropping an
    // option can make two lines identical, and a ticket showing the same dish
    // twice is one the kitchen cooks twice.
    const twin = items.find(
      (i) => i.product_id === product.id && selectionSignature(i.selections) === sig,
    )
    if (twin) {
      twin.quantity += line.quantity
      continue
    }
    items.push({
      id: seq(),
      product_id: product.id,
      product_name: product.name,
      quantity: line.quantity,
      unit_price: unitPrice,
      special_instructions: line.special_instructions ?? '',
      status: 'draft',
      category_id: product.category_id,
      course_number: line.course_number ?? 1,
      selections,
    })
  }
  return { items, dropped }
}
